import React from 'react';
import { Grid, Card, CardContent, Box, Typography, Avatar, Skeleton, alpha, useTheme } from '@mui/material';
import {
  TrendingUp,
  RateReview,
  CheckCircle,
  Speed,
} from '@mui/icons-material';

interface QuickStatsProps {
  transformationsThisWeek: number;
  readyForReview: number;
  completedCount: number;
  successRate: number;
  isLoading: boolean;
}

interface StatCardData {
  label: string;
  value: number | string;
  icon: React.ReactElement;
  colorKey: 'primary' | 'warning' | 'success' | 'info';
  suffix?: string;
  highlight?: boolean;
}

const QuickStats: React.FC<QuickStatsProps> = ({
  transformationsThisWeek,
  readyForReview,
  completedCount,
  successRate,
  isLoading,
}) => {
  const theme = useTheme();

  const cards: StatCardData[] = [
    {
      label: 'This Week',
      value: transformationsThisWeek,
      icon: <TrendingUp />,
      colorKey: 'primary',
    },
    {
      label: 'Ready for Review',
      value: readyForReview,
      icon: <RateReview />,
      colorKey: 'warning',
      highlight: readyForReview > 0,
    },
    {
      label: 'Completed',
      value: completedCount,
      icon: <CheckCircle />,
      colorKey: 'success',
    },
    {
      label: 'Success Rate',
      value: successRate,
      icon: <Speed />,
      colorKey: 'info',
      suffix: '%',
    },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 4 }}>
      {cards.map((card) => {
        const paletteColor = theme.palette[card.colorKey].main;
        return (
          <Grid item xs={6} md={3} key={card.label}>
            <Card
              variant="outlined"
              sx={{
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.2s',
                '&:hover': { borderColor: paletteColor, transform: 'translateY(-1px)' },
                ...(card.highlight && {
                  borderColor: alpha(paletteColor, 0.5),
                  bgcolor: alpha(paletteColor, 0.02),
                }),
              }}
            >
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                  <Box>
                    {isLoading ? (
                      <>
                        <Skeleton width={60} height={40} />
                        <Skeleton width={100} height={18} />
                      </>
                    ) : (
                      <>
                        <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.1 }}>
                          {card.value}{card.suffix ?? ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {card.label}
                        </Typography>
                      </>
                    )}
                  </Box>
                  <Avatar
                    sx={{
                      bgcolor: alpha(paletteColor, 0.1),
                      color: paletteColor,
                      width: 40,
                      height: 40,
                    }}
                  >
                    {card.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default QuickStats;
